import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { IconButton, Tooltip } from '@mui/material'
import RotateRightIcon from '@mui/icons-material/RotateRight'
import Lightbox from 'yet-another-react-lightbox'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails'
import type { RenderSlideContainerProps } from 'yet-another-react-lightbox'
import 'yet-another-react-lightbox/styles.css'
import 'yet-another-react-lightbox/plugins/thumbnails.css'

const LIGHTBOX_ZOOM = {
  maxZoomPixelRatio: 10,
  zoomInMultiplier: 2,
  doubleTapDelay: 300,
  doubleClickDelay: 300,
  doubleClickMaxStops: 2,
  keyboardMoveDistance: 50,
  wheelZoomDistanceFactor: 100,
  pinchZoomDistanceFactor: 100,
  scrollToZoom: true,
} as const

const LIGHTBOX_PLUGINS = [Zoom, Thumbnails]
const LIGHTBOX_CAROUSEL = { finite: true } as const
const LIGHTBOX_STYLES = { root: { zIndex: 15000 } } as const

const ROTATE_ATTR = 'data-yarl-rotate'

const resetSlideRotations = () => {
  document.querySelectorAll<HTMLElement>(`[${ROTATE_ATTR}]`).forEach((el) => {
    el.style.transform = ''
  })
}

export type ImagePreviewLightboxProps = {
  open: boolean
  onClose: () => void
  images: string[]
  index?: number
}

const ImagePreviewLightbox: React.FC<ImagePreviewLightboxProps> = ({
  open,
  onClose,
  images,
  index: initialIndex = 0,
}) => {
  const rotationRef = useRef(0)
  const startIndexRef = useRef(initialIndex)

  const imagesKey = useMemo(
    () => Array.from(new Set(images.filter(Boolean))).join('\u0001'),
    [images],
  )

  const slides = useMemo(
    () =>
      (imagesKey ? imagesKey.split('\u0001') : []).map((src) => ({
        src,
        width: 1920,
        height: 1080,
      })),
    [imagesKey],
  )

  useEffect(() => {
    if (!open) return
    startIndexRef.current = initialIndex
    rotationRef.current = 0
    resetSlideRotations()
  }, [open, initialIndex])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const handleView = useCallback(() => {
    rotationRef.current = 0
    resetSlideRotations()
  }, [])

  const handleRotate = useCallback(() => {
    rotationRef.current = (rotationRef.current + 90) % 360
    const el = document.querySelector<HTMLElement>(`.yarl__slide_current [${ROTATE_ATTR}]`)
    if (el) el.style.transform = `rotate(${rotationRef.current}deg)`
  }, [])

  const renderSlideContainer = useCallback(({ children }: RenderSlideContainerProps) => {
    return (
      <div
        data-yarl-rotate=""
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.2s ease',
        }}
      >
        {children}
      </div>
    )
  }, [])

  const toolbarButtons = useMemo(
    () => [
      <Tooltip key="image-preview-rotate" title="Xoay">
        <IconButton
          type="button"
          onClick={handleRotate}
          aria-label="Xoay"
          sx={{ color: 'common.white' }}
          size="small"
        >
          <RotateRightIcon fontSize="small" />
        </IconButton>
      </Tooltip>,
      'close' as const,
    ],
    [handleRotate],
  )

  const onHandlers = useMemo(() => ({ view: handleView }), [handleView])
  const renderProp = useMemo(() => ({ slideContainer: renderSlideContainer }), [renderSlideContainer])
  const toolbar = useMemo(() => ({ buttons: toolbarButtons }), [toolbarButtons])

  if (!open || slides.length === 0) return null

  const safeIndex = Math.min(Math.max(startIndexRef.current, 0), slides.length - 1)

  return (
    <Lightbox
      open
      close={handleClose}
      slides={slides}
      index={safeIndex}
      carousel={LIGHTBOX_CAROUSEL}
      plugins={LIGHTBOX_PLUGINS}
      zoom={LIGHTBOX_ZOOM}
      on={onHandlers}
      toolbar={toolbar}
      render={renderProp}
      styles={LIGHTBOX_STYLES}
    />
  )
}

export default ImagePreviewLightbox
